import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { z } from "zod";

import { withJWTClient } from "../connectClient";

import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const SPHINX_GATE_URL: string = import.meta.env.VITE_SPHINX_GATE_URL || "";

export const ROLE_STAFF = "org-staff";
export const ROLE_OWNER = "owner";
export const ROLE_MEMBER = "member";

export type ManageableRole = "owner" | "member";

export function toManageableRole(value: string): ManageableRole {
  return value === ROLE_OWNER ? "owner" : "member";
}

export const userKeys = {
  all: () => ["users"] as const,
  organizationUsers: (orgId: string) =>
    ["users", "organizationUsers", orgId] as const,
  organizationInvitations: (orgId: string) =>
    ["users", "organizationInvitations", orgId] as const,
  siteAccountOrg: (siteId: string) =>
    ["users", "siteAccountOrg", siteId] as const,
};

// sphinx-gate returns role slugs as a `roles []string` (may include
// "org-staff") plus an optional `last_sign_in_at`; the old singular
// `role` enum stripped both, so parse the real wire shape here.
export const organizationUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email_verified: z.boolean(),
  roles: z.array(z.string()).optional().default([]),
  last_sign_in_at: z.string().nullish(),
});

export const listOrganizationUsersResponseSchema = z.object({
  users: z.array(organizationUserSchema),
});

export type OrganizationUser = z.infer<typeof organizationUserSchema>;
export type ListOrganizationUsersResponse = z.infer<
  typeof listOrganizationUsersResponseSchema
>;

export const invitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  state: z.string(),
  role: z.string().nullish(),
  expires_at: z.string(),
  created_at: z.string(),
});

export const listOrganizationInvitationsResponseSchema = z.object({
  invitations: z.array(invitationSchema),
});

export type Invitation = z.infer<typeof invitationSchema>;
export type ListOrganizationInvitationsResponse = z.infer<
  typeof listOrganizationInvitationsResponseSchema
>;

export const inviteTeamMemberRequestSchema = z.object({
  email: z.string().email(),
  org_id: z.string().trim().min(1),
  role: z.enum(["owner", "member"]).optional(),
});

export const inviteTeamMemberResponseSchema = z.object({
  id: z.string(),
  email: z.string(),
  org_id: z.string(),
  state: z.string(),
  accept_invitation_url: z.string(),
});

export type InviteTeamMemberRequest = z.infer<
  typeof inviteTeamMemberRequestSchema
>;
export type InviteTeamMemberResponse = z.infer<
  typeof inviteTeamMemberResponseSchema
>;

export function roleDisplayLabel(roles: string[]): string {
  if (roles.includes(ROLE_STAFF)) return "Super admin";
  if (roles.includes(ROLE_OWNER)) return "Client admin";
  return "Client user";
}

export function isStaff(roles: string[]): boolean {
  return roles.includes(ROLE_STAFF);
}

export function isOwner(roles: string[]): boolean {
  return roles.includes(ROLE_OWNER);
}

export async function listOrganizationUsers(
  orgId: string,
): Promise<ListOrganizationUsersResponse> {
  const response = await withJWTClient((client) =>
    client.get(
      `${SPHINX_GATE_URL}/v1/organizations/${orgId}/users?include=roles`,
    ),
  );
  return listOrganizationUsersResponseSchema.parse(response.data);
}

export const organizationUsersQueryOptions = (
  orgId: string,
  enabled = !!orgId,
) => ({
  queryKey: userKeys.organizationUsers(orgId),
  queryFn: () => listOrganizationUsers(orgId),
  enabled,
  staleTime: 5 * 60 * 1000,
});

export function useOrganizationUsers(orgId: string, enabled = !!orgId) {
  return useQuery(organizationUsersQueryOptions(orgId, enabled));
}

export async function listOrganizationInvitations(
  orgId: string,
): Promise<ListOrganizationInvitationsResponse> {
  const response = await withJWTClient((client) =>
    client.get(
      `${SPHINX_GATE_URL}/v1/organizations/${orgId}/invitations?state=pending`,
    ),
  );
  return listOrganizationInvitationsResponseSchema.parse(response.data);
}

export const organizationInvitationsQueryOptions = (
  orgId: string,
  enabled = !!orgId,
) => ({
  queryKey: userKeys.organizationInvitations(orgId),
  queryFn: () => listOrganizationInvitations(orgId),
  enabled,
  staleTime: 5 * 60 * 1000,
});

export function useOrganizationInvitations(orgId: string, enabled = !!orgId) {
  return useQuery(organizationInvitationsQueryOptions(orgId, enabled));
}

export const siteAccountOrgSchema = z.object({
  account_org_id: z.string(),
});

export type SiteAccountOrg = z.infer<typeof siteAccountOrgSchema>;

// Resolves a site's owning WorkOS account org so elevated/managing-org viewers
// can drive the org-scoped user hooks against the account rather than their own
// login org. sphinx-gate returns 404 for both unmapped sites and unauthorized
// callers — that means "no manageable org for this viewer"; any other error is
// transient and should surface as a retryable failure.
export function isNoManageableOrgError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

export async function getSiteAccountOrg(
  siteId: string,
): Promise<SiteAccountOrg> {
  const response = await withJWTClient((client) =>
    client.get(`${SPHINX_GATE_URL}/v1/sites/${siteId}/account`),
  );
  return siteAccountOrgSchema.parse(response.data);
}

export const siteAccountOrgQueryOptions = (
  siteId: string,
  enabled = !!siteId,
) => ({
  queryKey: userKeys.siteAccountOrg(siteId),
  queryFn: () => getSiteAccountOrg(siteId),
  enabled,
  retry: false,
  staleTime: 5 * 60 * 1000,
});

export function useSiteAccountOrg(siteId: string, enabled = !!siteId) {
  return useQuery(siteAccountOrgQueryOptions(siteId, enabled));
}

export async function inviteTeamMember(
  payload: InviteTeamMemberRequest,
): Promise<InviteTeamMemberResponse> {
  const response = await withJWTClient((client) =>
    client.post(
      `${SPHINX_GATE_URL}/v1/users`,
      inviteTeamMemberRequestSchema.parse(payload),
    ),
  );
  return inviteTeamMemberResponseSchema.parse(response.data);
}

export function useInviteTeamMember(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation<InviteTeamMemberResponse, Error, InviteTeamMemberRequest>({
    mutationKey: ["inviteTeamMember", orgId],
    mutationFn: (payload) => inviteTeamMember(payload),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: userKeys.organizationUsers(variables.org_id),
      });
      void queryClient.invalidateQueries({
        queryKey: userKeys.organizationInvitations(variables.org_id),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to invite team member");
    },
  });
}

export async function revokeInvitation(
  orgId: string,
  invitationId: string,
): Promise<Invitation> {
  const response = await withJWTClient((client) =>
    client.post(
      `${SPHINX_GATE_URL}/v1/organizations/${orgId}/invitations/${invitationId}/revoke`,
    ),
  );
  return invitationSchema.parse(response.data);
}

export function useRevokeInvitation(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation<Invitation, Error, string>({
    mutationKey: ["revokeInvitation", orgId],
    mutationFn: (invitationId) => revokeInvitation(orgId, invitationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userKeys.organizationInvitations(orgId),
      });
      void queryClient.invalidateQueries({
        queryKey: userKeys.organizationUsers(orgId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to revoke invitation");
    },
  });
}

export async function resendInvitation(
  orgId: string,
  invitationId: string,
): Promise<Invitation> {
  const response = await withJWTClient((client) =>
    client.post(
      `${SPHINX_GATE_URL}/v1/organizations/${orgId}/invitations/${invitationId}/resend`,
    ),
  );
  return invitationSchema.parse(response.data);
}

export function useResendInvitation(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation<Invitation, Error, string>({
    mutationKey: ["resendInvitation", orgId],
    mutationFn: (invitationId) => resendInvitation(orgId, invitationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userKeys.organizationInvitations(orgId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to resend invitation");
    },
  });
}

export async function updateUserRole(
  orgId: string,
  userId: string,
  role: ManageableRole,
): Promise<void> {
  await withJWTClient((client) =>
    client.put(
      `${SPHINX_GATE_URL}/v1/organizations/${orgId}/users/${userId}/role`,
      { role },
    ),
  );
}

export function useUpdateUserRole(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { userId: string; role: ManageableRole }>({
    mutationKey: ["updateUserRole", orgId],
    mutationFn: ({ userId, role }) => updateUserRole(orgId, userId, role),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userKeys.organizationUsers(orgId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to update role");
    },
  });
}

export async function removeUser(orgId: string, userId: string): Promise<void> {
  await withJWTClient((client) =>
    client.delete(
      `${SPHINX_GATE_URL}/v1/organizations/${orgId}/users/${userId}`,
    ),
  );
}

export function useRemoveUser(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationKey: ["removeUser", orgId],
    mutationFn: (userId) => removeUser(orgId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userKeys.organizationUsers(orgId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to remove user");
    },
  });
}
